import React, { useState, useEffect, useCallback } from "react";
import Layout from "../Layout";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { BiChevronLeft, BiChevronRight, BiPlus, BiTime } from "react-icons/bi";
import { HiOutlineViewGrid } from "react-icons/hi";
import { HiOutlineCalendarDays } from "react-icons/hi2";
import AddAppointmentModal from "../components/Modals/AddApointmentModal";
import { servicesData } from "../components/Datas";
import axios from "axios";
import { BaseUrl } from "../BaseUrl";
import toast from "react-hot-toast";
import { Select } from "../components/Form";

// custom toolbar
const CustomToolbar = (toolbar) => {
  // today button handler
  const goToBack = () => {
    toolbar.date.setMonth(toolbar.date.getMonth() - 1);
    toolbar.onNavigate("prev");
  };

  // next button handler
  const goToNext = () => {
    toolbar.date.setMonth(toolbar.date.getMonth() + 1);
    toolbar.onNavigate("next");
  };

  // today button handler
  const goToCurrent = () => {
    toolbar.onNavigate("TODAY");
  };

  // month button handler
  const goToMonth = () => {
    toolbar.onView("month");
  };

  // week button handler
  const goToWeek = () => {
    toolbar.onView("week");
  };

  // day button handler
  const goToDay = () => {
    toolbar.onView("day");
  };

  // view button group
  const viewNamesGroup = [
    { view: "month", label: "Month" },
    { view: "week", label: "Week" },
    { view: "day", label: "Day" },
  ];

  return (
    <div className='mb-8 flex flex-col gap-8'>
      <div className='flex items-center justify-between'>
        <h1 className='text-xl font-semibold'>Appointments</h1>
        {toolbar.children}
      </div>

      <div className='grid gap-4 sm:grid-cols-2 md:grid-cols-12'>
        <div className='flex items-center justify-center sm:justify-start md:col-span-1'>
          <button
            onClick={goToCurrent}
            className='rounded-md border border-[#0095D9] px-6 py-2 text-[#0095D9]'
          >
            Today
          </button>
        </div>
        {/* label */}
        <div className='flex-rows gap-4 md:col-span-9'>
          <button onClick={goToBack} className='text-2xl text-[#0095D9]'>
            <BiChevronLeft />
          </button>
          <span className='text-xl font-semibold'>
            {moment(toolbar.date).format("MMMM YYYY")}
          </span>
          <button onClick={goToNext} className='text-2xl text-[#0095D9]'>
            <BiChevronRight />
          </button>
        </div>
        {/* filter */}
        <div className='grid grid-cols-3 rounded-md border border-[#0095D9] md:col-span-2'>
          {viewNamesGroup.map((item, index) => (
            <button
              key={index}
              onClick={
                item.view === "month"
                  ? goToMonth
                  : item.view === "week"
                    ? goToWeek
                    : goToDay
              }
              className={`flex-colo border-l border-[#0095D9] py-2 text-xl ${
                toolbar.view === item.view
                  ? "bg-[#0095D9] text-white"
                  : "text-[#0095D9]"
              }`}
            >
              {item.view === "month" ? (
                <HiOutlineViewGrid />
              ) : item.view === "week" ? (
                <HiOutlineCalendarDays />
              ) : (
                <BiTime />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

function Appointments() {
  const localizer = momentLocalizer(moment);
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState({});
  const [events, setEvents] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState({
    _id: "",
    fullName: "All Doctors",
  });

  // Fetch doctors for filter
  const fetchDoctors = async () => {
    try {
      const response = await axios.get(
        `${BaseUrl}/facility/getAllProfessionalUsers`,
        {
          withCredentials: true,
        }
      );
      if (response.data?.data) {
        setDoctors([
          { _id: "", fullName: "All Doctors" },
          ...response.data.data,
        ]);
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  // Fetch appointments
  const fetchAppointments = useCallback(async () => {
    try {
      let url = `${BaseUrl}/facility/appointments`;
      if (selectedDoctor._id) {
        url += `?doctorFilter=${selectedDoctor._id}`;
      }

      const response = await axios.get(url, { withCredentials: true });

      if (response.data?.data) {
        const mappedEvents = response.data.data.map((appt) => {
          // Parse date and time
          const dateStr = new Date(appt.dateOfVisit)
            .toISOString()
            .split("T")[0];
          const startDateTime = new Date(`${dateStr}T${appt.startTime}:00`);
          const endDateTime = new Date(`${dateStr}T${appt.endTime}:00`);

          return {
            id: appt._id,
            title: appt.userId?.fullName || "Unknown Patient",
            start: startDateTime,
            end: endDateTime,
            resource: appt,
            color:
              appt.status === "Scheduled"
                ? "#FB923C"
                : appt.status === "Completed"
                  ? "#66B5A3"
                  : "#FC8181",
            message: appt.purposeOfVisit,
            status: appt.status,
          };
        });
        setEvents(mappedEvents);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Could not load appointments");
    }
  }, [selectedDoctor]);

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // handle modal close
  const handleClose = () => {
    setOpen(!open);
    setData({});
    if (open) fetchAppointments(); // Refresh on close
  };

  // onClick event handler
  const handleEventClick = (event) => {
    setData(event.resource);
    setOpen(!open);
  };

  return (
    <Layout>
      {open && (
        <AddAppointmentModal
          datas={data}
          isOpen={open}
          closeModal={handleClose}
        />
      )}

      {/* calender */}
      <button
        onClick={() => {
          setData({});
          setOpen(true);
        }}
        className='flex-colo button-fb fixed bottom-8 right-12 z-50 h-16 w-16 animate-bounce rounded-full border border-border bg-[#0095D9] text-white'
      >
        <BiPlus className='text-2xl' />
      </button>

      <div className='mb-6 rounded-xl border border-border bg-white p-6'>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor='start'
          endAccessor='end'
          style={{
            // height fix screen
            height: 900,
            marginBottom: 50,
          }}
          onSelectEvent={(event) => handleEventClick(event)}
          defaultDate={new Date()}
          timeslots={1}
          resizable
          step={60}
          selectable={true}
          // custom event style
          eventPropGetter={(event) => {
            const style = {
              backgroundColor: event.color,
              borderRadius: "10px",
              color: "white",
              border: "1px",
              borderColor: "#F2FAF8",
              fontSize: "12px",
              padding: "5px 5px",
            };
            return {
              style,
            };
          }}
          // custom date style
          dayPropGetter={(date) => {
            const backgroundColor = "white";
            const style = {
              backgroundColor,
            };
            return {
              style,
            };
          }}
          // remove agenda view
          views={["month", "day", "week"]}
          toolbar={true}
          components={{
            toolbar: (props) => (
              <CustomToolbar {...props}>
                <div className='w-64'>
                  <select
                    className='w-full rounded-md border border-border px-4 py-2 text-sm text-textGray focus:border-[#0095D9] focus:outline-none'
                    value={selectedDoctor._id}
                    onChange={(e) => {
                      const doc = doctors.find((d) => d._id === e.target.value);
                      setSelectedDoctor(
                        doc || { _id: "", fullName: "All Doctors" }
                      );
                    }}
                  >
                    {doctors.map((doctor) => (
                      <option key={doctor._id} value={doctor._id}>
                        {doctor.fullName}
                      </option>
                    ))}
                  </select>
                </div>
              </CustomToolbar>
            ),
          }}
        />
      </div>
    </Layout>
  );
}

export default Appointments;
